import { Badge, Box, Button, Group, Loader, Stack, Text, TextInput, Title } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { modals } from '@mantine/modals';
import { IconCheck, IconTrash } from '@tabler/icons-react';
import { UpsellIcon } from 'components/UpsellIcon';
import { i18nKeys, useTranslation } from 'locales';
import { useState } from 'react';
import {
  type EmailSendingPreferences,
  useCreateEmailDomain,
  useDeleteEmailDomain,
  useLoadEmailDomain,
  useVerifyEmailDomain,
} from '../api';
import { DnsRecordsModal } from './DnsRecordsModal';
import classes from './EmailDomainSection.module.css';

// The sender Recovr falls back to when no custom domain is authenticated.
// Mirrors CompanyDetail#default_from_email on the backend.
const FALLBACK_SENDER = 'no-reply@email-recovr.eu';

// https://regexr.com/3au3g — the same shape the backend accepts.
const DOMAIN_FORMAT =
  /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/;

type EmailDomainSectionProps = {
  form: UseFormReturnType<EmailSendingPreferences>;
  canUseCustomDomain: boolean;
};

export const EmailDomainSection = ({ form, canUseCustomDomain }: EmailDomainSectionProps) => {
  const { t } = useTranslation();
  const [newDomain, setNewDomain] = useState('');

  const { emailDomain, isEmailDomainLoading } = useLoadEmailDomain();
  const { createEmailDomain, isCreateEmailDomainLoading } = useCreateEmailDomain();
  const { verifyEmailDomain, isVerifyEmailDomainLoading } = useVerifyEmailDomain();
  const { deleteEmailDomain, isDeleteEmailDomainLoading } = useDeleteEmailDomain();

  const domain = emailDomain?.domain ?? null;
  const isDomainValid = emailDomain?.domainValid ?? false;

  const openDnsRecords = (domainName: string) =>
    modals.open({
      title: t(i18nKeys.CUSTOM_DOMAIN.DNS_MODAL_TITLE, { domain: domainName }),
      size: 'lg',
      children: <DnsRecordsModal />,
    });

  const confirmDelete = () =>
    modals.openConfirmModal({
      title: t(i18nKeys.CUSTOM_DOMAIN.DELETE),
      children: <Text size="sm">{t(i18nKeys.AYS.DELETE_DOMAIN)}</Text>,
      labels: { confirm: t(i18nKeys.DELETE), cancel: t(i18nKeys.CANCEL) },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteEmailDomain(),
    });

  const header = (
    <Group justify="space-between" align="start" wrap="nowrap">
      <Stack gap={2}>
        <Group gap="xs">
          <Title order={6}>{t(i18nKeys.CUSTOM_DOMAIN.TITLE)}</Title>
          {canUseCustomDomain ? null : <UpsellIcon />}
        </Group>
        <Text size="xs" c="dimmed" maw={460}>
          {domain
            ? t(i18nKeys.CUSTOM_DOMAIN.DESCRIPTION)
            : t(i18nKeys.CUSTOM_DOMAIN.EMPTY_HINT, { email: FALLBACK_SENDER })}
        </Text>
      </Stack>
      {domain && (
        <Badge
          variant={isDomainValid ? 'light' : 'default'}
          color={isDomainValid ? 'validation' : undefined}
          radius="xl"
          style={{ flex: 'none' }}
          leftSection={isDomainValid ? <IconCheck size={12} /> : undefined}
        >
          {t(
            isDomainValid
              ? i18nKeys.CUSTOM_DOMAIN.STATUS_VERIFIED
              : i18nKeys.CUSTOM_DOMAIN.STATUS_PENDING,
          )}
        </Badge>
      )}
    </Group>
  );

  if (isEmailDomainLoading) {
    return (
      <Box bg="gray.0" bdrs="lg" p="lg" bd="1px solid var(--mantine-color-gray-2)">
        <Group justify="center">
          <Loader size="sm" />
        </Group>
      </Box>
    );
  }

  if (!domain) {
    return (
      <Stack
        gap="md"
        bg="gray.0"
        bdrs="lg"
        p="lg"
        bd="1px dashed var(--mantine-color-gray-3)"
        opacity={canUseCustomDomain ? 1 : 0.5}
      >
        {header}
        <Group align="end" gap="sm">
          <TextInput
            flex={1}
            label={t(i18nKeys.CUSTOM_DOMAIN.NAME)}
            placeholder="example.com"
            value={newDomain}
            onChange={event => setNewDomain(event.currentTarget.value.trim())}
            disabled={!canUseCustomDomain}
          />
          <Button
            onClick={() => createEmailDomain({ domain: newDomain })}
            disabled={!canUseCustomDomain || !DOMAIN_FORMAT.test(newDomain)}
            loading={isCreateEmailDomainLoading}
          >
            {t(i18nKeys.CUSTOM_DOMAIN.AUTHENTICATE)}
          </Button>
        </Group>
      </Stack>
    );
  }

  return (
    <Stack gap="md" bg="gray.0" bdrs="lg" p="lg" bd="1px solid var(--mantine-color-gray-2)">
      {header}
      {!isDomainValid && (
        <Text size="xs" c="dimmed">
          {t(i18nKeys.CUSTOM_DOMAIN.PENDING_HINT)}
        </Text>
      )}
      <Box className={classes.fieldsContainer}>
        <Box className={classes.fields}>
          <TextInput
            className={classes.field}
            label={t(i18nKeys.CUSTOM_DOMAIN.NAME)}
            value={domain}
            disabled
            readOnly
          />
          <TextInput
            className={classes.field}
            label={t(i18nKeys.CUSTOM_DOMAIN.DEFAULT_FROM)}
            rightSection={
              <Text size="sm" c="dimmed" pr="xs" style={{ whiteSpace: 'nowrap' }}>
                @{domain}
              </Text>
            }
            rightSectionWidth={`${domain.length * 8 + 24}px`}
            disabled={!isDomainValid}
            placeholder={isDomainValid ? '' : t(i18nKeys.CUSTOM_DOMAIN.NOT_YET_VERIFIED)}
            {...form.getInputProps('defaultFrom')}
          />
        </Box>
      </Box>
      <Group gap="sm" wrap="nowrap">
        <Button
          variant="light"
          color="red"
          leftSection={<IconTrash size={16} />}
          onClick={confirmDelete}
          loading={isDeleteEmailDomainLoading}
        >
          {t(i18nKeys.CUSTOM_DOMAIN.DELETE)}
        </Button>
        <Box flex={1} />
        <Button variant="default" onClick={() => openDnsRecords(domain)}>
          {t(i18nKeys.CUSTOM_DOMAIN.VIEW_DNS)}
        </Button>
        {!isDomainValid && (
          <Button
            variant="outline"
            onClick={() => verifyEmailDomain()}
            loading={isVerifyEmailDomainLoading}
          >
            {t(i18nKeys.CUSTOM_DOMAIN.VERIFY)}
          </Button>
        )}
      </Group>
    </Stack>
  );
};
